import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/ai")({ component: AiChat });

type Msg = { role: "user" | "assistant"; content: string };

const SUGGESTIONS = [
  "Bata Hutheesing Derasar ka itihas",
  "Aaj ki aarti aur pravachan timings?",
  "Ahimsa aur anekantvad ka matlab",
  "Best route to visit all 31 derasars",
];

function AiChat() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const SYSTEM = `You are "Yatra Sahayak" — a warm, respectful AI guide for the Jain Gaurav Yatra in Ahmedabad.
- Help devotees with the 31 Jain Derasars, aarti/pravachan timings, route planning, seva, volunteer & rickshaw bookings, certificates, and Jain dharma basics (ahimsa, anekantvad, aparigraha).
- Reply in the user's language (English / Hindi / Gujarati). Keep answers concise (2-5 short bullets or 3-4 sentences).
- Always begin with a brief Jai Jinendra greeting only on the very first reply.
- Never invent specific times for unknown temples — say "please check the temple page" instead.`;

  async function send(textArg?: string) {
    const text = (textArg ?? input).trim();
    if (!text || loading) {
      return;
    }
    setInput("");
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setLoading(true);

    try {
      const url =
        import.meta.env.VITE_AI_CHAT_URL || "https://ai.gateway.lovable.dev/v1/chat/completions";
      const key = import.meta.env.VITE_LOVABLE_API_KEY;
      if (!key) {
        toast.error("Add VITE_LOVABLE_API_KEY to your .env file to use AI chat.");
        setLoading(false);
        return;
      }
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
        body: JSON.stringify({
          model: "google/gemini-3-flash-preview",
          stream: true,
          messages: [{ role: "system", content: SYSTEM }, ...next],
        }),
      });

      if (resp.status === 429) {
        toast.error("AI is busy. Try again in a moment.");
        setLoading(false);
        return;
      }
      if (resp.status === 402) {
        toast.error("AI credits exhausted.");
        setLoading(false);
        return;
      }
      if (!resp.ok || !resp.body) {
        toast.error("AI failed");
        setLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      setMessages((p) => [...p, { role: "assistant", content: "" }]);

      let done = false;
      while (!done) {
        const r = await reader.read();
        if (r.done) {
          break;
        }
        buf += decoder.decode(r.value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          if (line.endsWith("\r")) {
            line = line.slice(0, -1);
          }
          if (!line.startsWith("data: ")) {
            continue;
          }
          const j = line.slice(6).trim();
          if (j === "[DONE]") {
            done = true;
            break;
          }
          try {
            const p = JSON.parse(j);
            const c = p.choices?.[0]?.delta?.content;
            if (c) {
              acc += c;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: acc };
                return copy;
              });
            }
          } catch {
            buf = line + "\n" + buf;
            break;
          }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("AI error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div
        className="relative overflow-hidden rounded-b-3xl px-5 pb-6 pt-14 text-primary-foreground"
        style={{ background: "var(--gradient-sunset)" }}
      >
        <Link
          to="/app"
          className="absolute left-4 top-12 rounded-full glass px-2.5 py-1 text-[11px] font-semibold text-foreground"
        >
          <ArrowLeft className="inline h-3 w-3" /> Back
        </Link>
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/25 backdrop-blur">
            <Sparkles className="h-4 w-4 animate-flicker" />
          </span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-90">
              AI Powered
            </div>
            <div className="font-serif text-xl font-bold">Yatra Sahayak</div>
          </div>
        </div>
        <p className="mt-2 text-[12px] opacity-95">
          Ask about temples, aarti, route, dharma, seva — in English, Hindi or Gujarati.
        </p>
      </div>

      {/* Chat */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <div className="space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Try asking
            </div>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="rounded-2xl border border-border bg-card px-3 py-2.5 text-left text-[12px] font-medium hover:border-primary hover:shadow-md transition-all"
                >
                  <Sparkles className="mr-1.5 inline h-3 w-3 text-primary" />
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm ${
                m.role === "user"
                  ? "rounded-br-md text-primary-foreground"
                  : "rounded-bl-md border border-border bg-card"
              }`}
              style={m.role === "user" ? { background: "var(--gradient-saffron)" } : undefined}
            >
              {m.content ||
                (loading && i === messages.length - 1 ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  ""
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className="border-t border-border bg-card/80 p-3 backdrop-blur">
        <div className="flex items-end gap-2 rounded-2xl border border-border bg-background px-2 py-1.5 shadow-sm focus-within:border-primary">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask Yatra Sahayak…"
            className="flex-1 resize-none bg-transparent px-2 py-1.5 text-[13px] outline-none"
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-primary-foreground disabled:opacity-50"
            style={{ background: "var(--gradient-saffron)" }}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-1.5 text-center text-[10px] text-muted-foreground">
          ✨ Powered by Lovable AI · Responses may be inaccurate
        </div>
      </div>
    </div>
  );
}
